// Validation Chain - Combines multiple validators
import type { Validator, ValidationContext, ValidationResult } from './types';

/**
 * Validator Chain - implements Chain of Responsibility pattern
 * Runs validators in sequence and stops at first failure
 */
export class ValidatorChain implements Validator {
  private validators: Validator[] = [];

  add(validator: Validator): ValidatorChain {
    this.validators.push(validator);
    return this;
  }

  async validate(context: ValidationContext): Promise<ValidationResult> {
    for (const validator of this.validators) {
      const result = await validator.validate(context);
      if (!result.isValid) {
        return result;
      }
    }
    return { isValid: true };
  }
}

/**
 * Composite Validator - runs all validators and collects all errors
 */
export class CompositeValidator implements Validator {
  private validators: Validator[] = [];

  add(validator: Validator): CompositeValidator {
    this.validators.push(validator);
    return this;
  }

  async validate(context: ValidationContext): Promise<ValidationResult> {
    const errors: string[] = [];

    for (const validator of this.validators) {
      const result = await validator.validate(context);
      if (!result.isValid && result.message) {
        errors.push(result.message);
      }
    }

    if (errors.length > 0) {
      return {
        isValid: false,
        message: errors[0] // Return first error for consistency
      };
    }

    return { isValid: true };
  }
}

